/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import React, { Suspense, lazy, useCallback, useContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "@i18n";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "@Reducers/index";
import { AiOutlineLeft } from "react-icons/ai";
import { PredicateBuilder, PredicateShape } from "@Services/queryBuilder";
import ViewIssue from "../Issue/viewIssue";
import { CubeGlobal, HoopsViewerContext } from "@Features/viewer-core";
import {
  getDefaultListOfStatusAvailable,
  getPdfMarkUpData,
  getfilteredIssuesData,
  isIssueRequiredPermission,
  issuesBcfImport,
} from "@Features/issues/state/bcfData.actions";
import { deleteIssueData } from "@Features/issues/state/deletingIssues.actions";
import { deleteIssuesPinData } from "@Features/issues/state/issuesPinDataSlice";
import { addAnnotation, setAnnotations, setModalStatus, updateViewerState } from "../../../../reducers/Viewer";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { BIM_ENGINE, CAD_ENGINE, PDF_ENGINE, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { cubeView } from "@Services/cubeView";
import { EXPORT_TYPE, exportPdf } from "@Services/fileDownload";
import _ from "lodash";
import { fileToBase64, getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import { showToastNotification } from "@Reducers/toastSlice";
import { useRotatedPdfIssueGuard } from "../../hooks/useRotatedPdfIssueGuard";
import { useLocation } from "react-router-dom";
import cloneObject from "@Services/cloneObject";
import { emptyIssuesData } from "@Features/issues/state/issues";
import Text from "@CubeComponents/html/Text/Text";
import { DisabledTooltip } from "@Components/DisabledTooptip";
import { createUuid } from "@ts3d-hoops/web-viewer";
import { folderPermissions } from "@Helpers/constants";
import type { IssueItem, GetPdfMarkUpDataRequest, GetIssuesDataRequest } from "@ServerTypes/issues/issues.schema";

const IssuesList = lazy(() => import("../IssuesList/IssuesList"));
const CreateIssue = lazy(() => import("../CreateIssue/CreateIssue"));

// Issues kept in redux state are always fully loaded: keyed by their `id` and
// carrying the viewer fields (`pinData`) this pallet reads as present. The server
// `IssueItem` types these optional because it serves multiple shapes; narrow once.
type LoadedIssue = IssueItem & { pinData: string };
type LoadedIssuesMap = Record<string, LoadedIssue>;

interface IssuesPalletProps {
  handleExportToBcf: (data: { issues: LoadedIssuesMap; selectedIssues: Record<string, boolean> }) => Promise<void>;
  onViewIssueClick: (issueId: string) => void;
  handleSelectedIssues: (issueId: string, deleteAll?: boolean) => void;
  fileType: string;
  engineType: string;
}

function IssuesPallet({
  handleExportToBcf,
  onViewIssueClick,
  handleSelectedIssues,
  fileType,
  engineType,
}: Readonly<IssuesPalletProps>) {
  const { t } = useTranslation();
  const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);
  const refuseIssueOnRotatedPdf = useRotatedPdfIssueGuard();
  const dispatch = useDispatch<AppDispatch>();
  const {
    fileVersionId,
    fileVersionData,
    selectedIssues,
    currentViewIssueId,
    transmittalVersionId,
    isModelStructureReady,
    viewerImpl,
    isSettingsApplied,
  } = useContext(HoopsViewerContext);
  const [searchIssue, setSearchIssue] = useState<boolean>(false);
  const [showFilterIssue, setShowFilterIssue] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<PredicateShape | Record<string, never>>({});
  const [filterPredicates, setFilterPredicates] = useState<PredicateShape | Record<string, never>>({});
  const [filterConditions, setFilterConditions] = useState<Record<string, string>>({});
  const [deleteEnabled, setDeleteEnabled] = useState<boolean>(true);
  const [openDeleteModalPopUp, setOpenDeleteModalPopUp] = useState<boolean>(false);
  const [openExportModalPopUp, setOpenExportModalPopUp] = useState<boolean>(false);
  const [sortCondition, setSortCondition] = useState<boolean>(false);
  const [sortPredicates, setSortPredicates] = useState<{ name: string; direction: string }[]>([]);
  const [handleExportBcfBtn, setHandleExportBcfBtn] = useState<boolean>(false);
  const [viewPdfMarkupToggle, setViewPdfMarkupToggle] = useState<boolean>(false);
  const [pdfMarkupData, setPdfMarkupData] = useState<IssueItem[]>([]);
  const [disableImportButton, setDisableImportButton] = useState<boolean>(false);
  const [selectedIssuesPermissions, setSelectedIssuesPermission] = useState({
    delete: true,
    export: true,
  });
  const issues = useSelector((state: RootState) => state.issues as LoadedIssuesMap);

  const inputFileRef = useRef<any>(null);
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const { RESOURCE_DELETE, RESOURCE_DOWNLOAD } = folderPermissions();

  useEffect(() => {
    dispatch(emptyIssuesData());
    handleSelectedIssues("removeAll", true);
  }, []);

  useEffect(
    () => () => {
      removeMarkupHandler();
    },

    [],
  );

  const removeMarkupHandler = () => {
    if (
      [BIM_ENGINE, CAD_ENGINE].includes(engineType) &&
      getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })
    ) {
      const activeMarkups = viewerImpl?.hwv.markupManager.getActiveMarkupView(viewerImpl?.hwv.view);
      if (activeMarkups) {
        viewerImpl?.hwv.markupManager.deleteMarkupView(activeMarkups._uniqueId);
      }
      cubeView.flushView();
    }
  };

  useEffect(() => {
    if (
      [BIM_ENGINE, CAD_ENGINE].includes(engineType) &&
      HoopsModalStatus.Issues?.show &&
      HoopsModalStatus.Issues?.currentStage == HoopsModalStatus.Issues?.defaultStage
    ) {
      removeMarkupHandler();
    }
  }, [HoopsModalStatus]);

  useEffect(() => {
    if (sortCondition) {
      setSortPredicates([{ name: "createdDate", direction: "ascending" }]);
    } else {
      setSortPredicates([{ name: "createdDate", direction: "descending" }]);
    }
  }, [sortCondition]);

  useEffect(() => {
    let allowDelete = true;
    let allowDownload = true;
    for (const issueId of Object.keys(selectedIssues)) {
      if (issues[issueId]?.issueType === "external") {
        setDeleteEnabled(false);
        break;
      }

      if (!isIssueRequiredPermission(issues[issueId], RESOURCE_DELETE.id)) {
        allowDelete = false;
      }
      if (!isIssueRequiredPermission(issues[issueId], RESOURCE_DOWNLOAD.id)) {
        allowDownload = false;
      }
    }
    setSelectedIssuesPermission((prev) => ({ ...prev, delete: allowDelete, export: allowDownload }));
  }, [issues, selectedIssues, RESOURCE_DELETE.id, RESOURCE_DOWNLOAD.id]);

  const handleSetFilterConditions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filterData: Record<string, string> = { ...filterConditions };
    if (e.target.value) {
      filterData[e.target.name] = e.target.value;
    } else {
      delete filterData[e.target.name];
    }
    setFilterConditions(filterData);
  };

  const handleOpenCreateIssue = () => {
    if (refuseIssueOnRotatedPdf("create")) return;
    dispatch(
      setModalStatus({
        ...HoopsModalStatus,
        Issues: {
          ...HoopsModalStatus.Issues,
          currentStage: "create_issue",
          backStage: HoopsModalStatus.Issues?.defaultStage,
        },
      }),
    );
  };

  const onInputChangePred = () => {
    if (Object.keys(filterConditions).length) {
      let predicate: PredicateBuilder | undefined;
      for (const filterKey of Object.keys(filterConditions)) {
        predicate = predicate
          ? predicate.and(filterKey, "contains", filterConditions[filterKey])
          : new PredicateBuilder(filterKey, "contains", filterConditions[filterKey]);
      }
      if (predicate) setFilterPredicates(predicate);
    }
  };

  const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
    const predicate = new PredicateBuilder("title", "contains", e.target.value);
    setSearchString(predicate);
  };

  const handleResetSearch = () => {
    setSearchString({});
    setSearchIssue(false);
  };

  const handleData = useCallback(async () => {
    const params: GetPdfMarkUpDataRequest = {
      fileId: [fileVersionId],
    };
    const pdfMarkupData = await getPdfMarkUpData(params);
    if (pdfMarkupData.data.status) {
      setPdfMarkupData(pdfMarkupData.data.result!);
    }
  }, [fileVersionId]);

  const issuesStage = HoopsModalStatus.Issues?.currentStage;

  useEffect(() => {
    if ([PDF_ENGINE].includes(engineType) && issuesStage === "issues") {
      handleData();
    }
  }, [engineType, issuesStage, handleData]);

  const handleIssueDelete = () => {
    if ([BIM_ENGINE, CAD_ENGINE].includes(engineType)) {
      const measurementIds: string[] = [];
      const allMeasurementsInViewer = viewerImpl?.hwv.measureManager.getAllMeasurements();
      for (const issueId of Object.keys(selectedIssues)) {
        const issue = issues[issueId];
        if (issue && issue.pinData != "{}") {
          measurementIds.push(JSON.parse(issue.pinData)._uniqueId);
        }
      }
      const markupsToRemove = allMeasurementsInViewer.filter((record: any) =>
        measurementIds.includes(record._uniqueId),
      );
      for (const markup of markupsToRemove) {
        viewerImpl?.hwv.measureManager.removeMeasurement(markup);
      }
      dispatch(deleteIssuesPinData(Object.keys(selectedIssues)));
    } else {
      setPdfMarkupData((prev) => prev.filter((data) => !Object.keys(selectedIssues).includes(data.id!)));
    }
    dispatch(deleteIssueData(Object.keys(selectedIssues)));
    for (const issueId of Object.keys(selectedIssues)) {
      handleSelectedIssues(issueId);
    }
    setOpenDeleteModalPopUp(false);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModalPopUp(false);
  };

  const handleDeleteButton = () => {
    setOpenDeleteModalPopUp(true);
  };

  const closeExportModal = () => {
    setOpenExportModalPopUp(false);
  };

  const handleExportButton = () => {
    setOpenExportModalPopUp(true);
  };

  const handleImportBCFButtonClick = () => {
    inputFileRef.current.click();
  };

  const handleExport = async () => {
    setHandleExportBcfBtn(true);
    await handleExportToBcf({ issues, selectedIssues });
    setHandleExportBcfBtn(false);
    closeExportModal();
  };

  const handleExportToPdfButton = async () => {
    const issuesIds: string[] = Object.keys(selectedIssues);
    exportPdf({ issuesIds, exportType: EXPORT_TYPE.PDF });
  };

  const showPdfMarkupsHandler = (args: React.ChangeEvent<HTMLInputElement>) => {
    // TODO: pradeep Quick patch as delete anotations are crashing again
    try {
      if (args.target.checked) {
        dispatch(updateViewerState({ field: "showAllIssueMarkups", value: true }));
        pdfMarkupData.map((markup) => {
          dispatch(addAnnotation({ data: JSON.parse(markup.viewpoint!), annotationType: "markupAnnotations" }));
        });
      } else {
        dispatch(updateViewerState({ field: "showAllIssueMarkups", value: false }));
        dispatch(setAnnotations({ data: [], annotationType: "markupAnnotations" }));
      }
      setViewPdfMarkupToggle(args.target.checked);
    } catch (e) {
      console.log(e);
      Promise.reject(e);
    }
  };

  const deletePopup = () => (
    <div className="flex flex-col gap-10">
      <ModalStateDialogComponent
        footerContent={
          <div className="flex w-full justify-between">
            <ButtonComponent onClick={closeDeleteModal} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
            <ButtonComponent onClick={handleIssueDelete} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
              <Text>{t("Delete")}</Text>
            </ButtonComponent>
          </div>
        }
        headerTitle={
          <span className="">
            <Text>{t("Delete all selected?")}</Text>
          </span>
        }
        isVisible={openDeleteModalPopUp}
        onCancel={closeDeleteModal}
        modelContent={
          <Text sx={{ whiteSpace: "normal" }}>{t("Are you sure you want to delete the selected items?")}</Text>
        }
        width="23.125rem"
      />
    </div>
  );

  const exportPopUp = () => (
    <div>
      <ModalStateDialogComponent
        footerContent={
          <div className="flex w-full justify-between">
            <ButtonComponent
              onClick={closeExportModal}
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              disabled={handleExportBcfBtn}
            >
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
            <ButtonComponent
              onClick={handleExport}
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              disabled={handleExportBcfBtn}
            >
              <Text>{t("Export")}</Text>
            </ButtonComponent>
          </div>
        }
        headerTitle={
          <span className="">
            <Text>{t("Export all selected as")}</Text>
          </span>
        }
        onCancel={closeExportModal}
        isVisible={openExportModalPopUp}
        modelContent={
          <div className="flex flex-col gap-8">
            <p>
              <Text>{t("Please select the format in which you want to export the selected issues")}</Text>
            </p>
            <div className="flex flex-col gap-2.5">
              <div>
                <RadioButton label="BCF" name="export" />
              </div>
              <div>
                <RadioButton label="BCFzip" name="export" />
              </div>
            </div>
          </div>
        }
        width="25rem"
      />
    </div>
  );

  const viewpointConvertorForBCF = (viewpointMap: any) => {
    if (!viewpointMap) {
      return null;
    }
    const viewpoint = {
      camera: viewpointMap.getCamera().toJson(),
      markup: [],
      cuttingData: {
        cappingFaceColor: { r: 128, g: 128, b: 128 },
        cappingGeometryVisibility: true,
        cappingLineColor: { r: 128, g: 128, b: 128 },
        cuttingSections: [],
        pickable: true,
      },
      colors: [],
      sheetId: null,
      explodeMagnitude: 0,
      lineVisibility: true,
      faceVisibility: true,
      defaultVisibility: true,
      visibilityExceptions: [],
      measurementsMarkup: [],
      selection: [],
      shading: 2,
      name: "",
      uniqueId: createUuid(),
      transparencies: { elements: [], value: 0.4 },
      modelTransfroms: [],
    };

    return JSON.stringify(viewpoint);
  };

  const getbcfData = (bcfData: any) => {
    if (!bcfData) {
      return null;
    }

    delete bcfData._viewer;
    bcfData = cloneObject(bcfData);
    return JSON.stringify(bcfData);
  };

  const snapshotConvertorForBCF = (snapshotunit8Array: any) => {
    if (!snapshotunit8Array) {
      return null;
    }
    let binaryString = "";
    for (const byte of snapshotunit8Array) {
      binaryString += String.fromCodePoint(byte);
    }
    const abc = btoa(binaryString);
    return `data:image/png;base64,${abc}`;
  };

  const commentsConvertorForBCF = (topicMarkup: any, topic: any) => {
    const comments = topicMarkup.getComments();
    const viewpointMap = topicMarkup.getViewpoints();
    const commentsList: any[] = [];
    for (const comment of comments) {
      const viewpointfileName = viewpointMap.get(comment._viewpointGuid)?._viewpointFilename;
      const snapshotfileName = viewpointMap.get(comment._viewpointGuid)?._snapshotFilename;
      commentsList.push({
        title: comment._text,
        createdBy: comment._author,
        createdDate: comment._date,
        snapshot: snapshotConvertorForBCF(topic.getSnapshot(snapshotfileName)?._data),
        viewpoint: viewpointConvertorForBCF(topic.getViewpoint(viewpointfileName)),
        bcfData: getbcfData(topic.getViewpoint(viewpointfileName)),
      });
    }
    return commentsList;
  };

  const getIssueStatusForBCF = ({ name, statusList }: { name: string; statusList: any[] }) => {
    let mappedValue: string | undefined;
    const statusMapper = [
      {
        key: ["in_review", "in review", "in_progress", "in progress"],
        value: "In Review",
      },
      {
        key: ["open", "active"],
        value: "Open",
      },
      {
        key: ["closed", "completed"],
        value: "Closed",
      },
    ];

    if (name) {
      name = name.toLowerCase();
      for (const { key, value } of statusMapper) {
        if (key.includes(name)) {
          mappedValue = value;
        }
      }
    }
    if (!mappedValue) {
      mappedValue = "Open";
    }
    return statusList.find((status: any) => status.name == mappedValue)._id;
  };

  const importIssues = async () => {
    const spaceId = urlSearchParams.get("spaceId");
    if (!spaceId) return;
    let statusList: any = await getDefaultListOfStatusAvailable({
      spaceId,
      entityType: "ISSUES",
    });
    if (!statusList.data.status || !statusList.data.result.length) {
      dispatch(
        showToastNotification({
          content: t("Status not found!"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      return;
    }
    statusList = statusList.data.result;
    const importIssuesData: any[] = [];
    const data = viewerImpl?.hwv.BCFManager.getBCFMap();
    for (const mappedData of data) {
      const allTopics = mappedData.getTopics();
      for (const topic of allTopics) {
        const topicMarkup = topic.getMarkup();
        importIssuesData.push({
          createdBy: topicMarkup.getTopicCreationAuthor(),
          createdDate: topicMarkup.getTopicCreationDate(),
          description: topicMarkup.getTopicDescription(),
          // modifiedDate : topicMarkup.getTopicModifiedDate(),
          // modifiedBy : topicMarkup.getTopicModifiedAuthor(),
          title: topicMarkup.getTopicTitle(),
          status: getIssueStatusForBCF({
            name: topicMarkup.getTopicStatus(),
            statusList,
          }),
          dueDate: topicMarkup.getTopicDueDate(),
          comments: commentsConvertorForBCF(topicMarkup, topic),
        });

        importIssuesData.at(-1).snapshot = snapshotConvertorForBCF(topic.getSnapshot("snapshot.png")?._data);
        importIssuesData.at(-1).viewpoint = viewpointConvertorForBCF(topic.getViewpoint("viewpoint.bcfv"));
        importIssuesData.at(-1).bcfData = getbcfData(topic.getViewpoint("viewpoint.bcfv"));
      }
    }

    const params: any = {
      issuesList: importIssuesData,
      fileId: fileVersionId,
      spaceId: urlSearchParams.get("spaceId"),
    };
    const response = await issuesBcfImport(params);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("BCF has been imported successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      const getParams: GetIssuesDataRequest = {
        fileId: [fileVersionId],
        value: {
          skip: 0,
          take: 5,
          requiresCounts: true,
        },
      };
      dispatch(getfilteredIssuesData(getParams));
    }
    for (const [key] of data) {
      viewerImpl?.hwv.BCFManager.removeBCFData(key);
    }
  };

  const handleBcfImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisableImportButton(true);
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      const fileBase64Str = await fileToBase64(file);
      await viewerImpl?.hwv.BCFManager.addBCFFromFile(fileBase64Str);
      await importIssues();
    } catch (e) {
      console.log(e);
      dispatch(
        showToastNotification({
          content: t("Invalid Format!"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      Promise.reject(e);
    } finally {
      inputFileRef.current.value = "";
      setDisableImportButton(false);
    }
  };

  const handleSortClick = () => {
    setSortCondition((prev) => !prev);
  };

  const resetFilterHandler = () => {
    setShowFilterIssue(false);
    setFilterPredicates({});
    setFilterConditions({});
  };

  const viewerLoaded = getIsViewerDependencyLoaded({
    isModelStructureReady,
    isSettingsApplied,
    viewerImpl,
    hwv: CubeGlobal.hwv,
  });
  return (
    <div className="flex h-full bg-white overflow-y-auto flex-col">
      {deletePopup()}
      {exportPopUp()}
      {HoopsModalStatus.Issues?.currentStage === "issues" && (
        <div
          className="flex min-h-8 flex-col bg-white p-4 shadow-[0_0_0.25rem_0_rgba(0,0,0,0.15)]"
          data-testid="palletHeader"
        >
          <div className="w-full flex items-center gap-2.5" data-testid="palletOptions">
            {Object.keys(selectedIssues).length === 0 && !searchIssue && (
              <div className="flex justify-between w-full">
                <div className="flex gap-2.5">
                  <DisabledTooltip
                    disabled={[BIM_ENGINE, CAD_ENGINE].includes(engineType) && !viewerLoaded}
                    tooltip={t("Please wait for model to load")}
                  >
                    <ButtonComponent
                      buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                      onClick={handleOpenCreateIssue}
                      disabled={[BIM_ENGINE, CAD_ENGINE].includes(engineType) && !viewerLoaded}
                    >
                      <Text>{t("Create New")}</Text>
                    </ButtonComponent>
                  </DisabledTooltip>
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={() => setShowFilterIssue(!showFilterIssue)}
                  >
                    <span className="flex items-center gap-2.5" data-testid="filterBtnDropdown">
                      <IconComponent name="Filter" width="1rem" height="1rem" />
                      <span data-testid="filterBtnCreateIssue">
                        <Text>{t("Filter")}</Text>
                      </span>
                      {Object.keys(filterPredicates).length > 0 && Object.keys(filterConditions).length > 0 && (
                        <span className="rounded-xl text-xs px-3 bg-yet_to_start_communication text-white">
                          {Object.keys(filterConditions).length}
                        </span>
                      )}
                    </span>
                  </ButtonComponent>
                  <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleSortClick}>
                    <span className="flex items-center gap-2.5" data-testid="sortBtnDropdown">
                      <img src="/assets/icons/svg/sort-icon.svg" className="w-3 h-3" alt="" />
                      <span className="text-sm" data-testid="sortBtnCreateIssue">
                        <Text>{t("Sort")}</Text>
                      </span>
                    </span>
                  </ButtonComponent>
                  {[BIM_ENGINE, CAD_ENGINE].includes(engineType) && (
                    <>
                      <ButtonComponent
                        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                        onClick={handleImportBCFButtonClick}
                        disabled={disableImportButton}
                      >
                        <span className="flex items-center gap-2.5" data-testid="importBCF">
                          <IconComponent name="ImportExcel" width="1rem" height="1rem" />

                          <span className="pe-1" data-testid="importText">
                            <Text>{t("Import BCF")}</Text>
                          </span>
                        </span>
                      </ButtonComponent>
                      <input
                        ref={inputFileRef}
                        type="file"
                        accept=".bcf,.bcfzip"
                        id="bcfImport"
                        onChange={handleBcfImport}
                        className="hidden"
                      />
                    </>
                  )}
                  {[PDF_ENGINE].includes(engineType) && (
                    <ToolTipComponent
                      showTipPointer
                      position="bottom"
                      content={t("This will show markup of all issues.")}
                    >
                      <ToggleSwitchComponent
                        change={showPdfMarkupsHandler}
                        onChange={showPdfMarkupsHandler}
                        checked={viewPdfMarkupToggle}
                      />
                    </ToolTipComponent>
                  )}
                </div>
                <div
                  className="flex h-8 items-center justify-end text-sm font-semibold text-[#192048] cursor-pointer"
                  data-testid="issuesSearchButton"
                >
                  <IconComponent
                    name="SearchIcon"
                    onClick={() => {
                      setSearchIssue(true);
                    }}
                    width="1.25rem"
                    height="1.25rem"
                  />
                </div>
              </div>
            )}

            {Object.keys(selectedIssues).length > 0 && (
              <>
                {deleteEnabled && (
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={handleDeleteButton}
                    disabled={!selectedIssuesPermissions.delete}
                  >
                    <span className="flex items-center gap-2.5" data-testid="deleteButton">
                      <img src="/assets/icons/svg/mi_delete.svg" alt="" />
                      <span data-testid="deleteText">
                        <Text>{t("Delete")}</Text>
                      </span>
                    </span>
                  </ButtonComponent>
                )}
                {![PDF_ENGINE].includes(engineType) && (
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={handleExportButton}
                    disabled={!selectedIssuesPermissions.export}
                  >
                    <span className="flex items-center gap-2.5" data-testid="exportButton">
                      <img src="/assets/icons/svg/fontisto_export.svg" alt="" />
                      <span data-testid="exportText">
                        <Text>{t("Export")}</Text>
                      </span>
                    </span>
                  </ButtonComponent>
                )}
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                  onClick={handleExportToPdfButton}
                  disabled={!selectedIssuesPermissions.export}
                >
                  <span className="flex items-center gap-2.5" data-testid="exportToPdfButton">
                    <img src="/assets/icons/svg/fontisto_export.svg" alt="" />
                    <span data-testid="exportToPdfText">
                      <Text>{t("Export To Pdf")}</Text>
                    </span>
                  </span>
                </ButtonComponent>
              </>
            )}

            {searchIssue && (
              <>
                <span>
                  <AiOutlineLeft style={{ cursor: "pointer" }} onClick={handleResetSearch} />
                </span>
                <input className="e-input" onChange={handleSetSearchString} placeholder={t("Search For Issue")} />
              </>
            )}

            {/* {false && Object.keys(selectedIssues).length > 0 && (
              <span
                onClick={handleDelete}
                style={{ marginInlineStart: "1rem", cursor: "pointer" }}
              >
                <img
                  src={`${getImageUrl()}/delete.svg`}
                />
              </span>
            )} */}
          </div>

          {showFilterIssue && (
            <div className="flex flex-col gap-5 bg-[#f0f5f9] p-5">
              <div className="flex flex-row gap-4 bg-[#f0f5f9]">
                <div className="flex w-full flex-col gap-4" data-testid="leftPartFilterIssues">
                  <div
                    className="flex flex-row items-center text-sm text-[#192048]"
                    data-testid="issueTitleFilterIssues"
                  >
                    <div style={{ width: "100%" }} data-testid="issueTitleTextFilterIssues">
                      <Text>{t("Issue Title")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.title ?? ""}
                      name="title"
                      placeholder={t("Issue Title")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  <div className="flex flex-row items-center text-sm text-[#192048]" data-testid="statusFilterIssues">
                    <div style={{ width: "100%" }} data-testid="statusTextFilterIssues">
                      <Text>{t("Status")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.status ?? ""}
                      name="status"
                      placeholder={t("Status")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  <div
                    className="flex flex-row items-center text-sm text-[#192048]"
                    data-testid="createdByFilterIssues"
                  >
                    <div style={{ width: "100%" }}>
                      <Text>{t("Created by")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.createdByName ?? ""}
                      name="createdByName"
                      placeholder={t("Created by")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  <div className="flex flex-row items-center text-sm text-[#192048]" data-testid="versionFilterIssues">
                    <div style={{ width: "100%" }}>
                      <Text>{t("Version")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.fileVersionNumber ?? ""}
                      name="fileVersionNumber"
                      placeholder={t("Version")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                </div>
                <div className="flex w-full flex-col gap-4" data-testid="rightPartFilterIssues">
                  <div className="flex flex-row items-center text-sm text-[#192048]" data-testid="issueIDFilterIssues">
                    <div style={{ width: "100%" }} data-testid="issueIDTextFilterIssues">
                      <Text>{t("Issue ID")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.issueCounter ?? ""}
                      name="issueCounter"
                      placeholder={t("Issue ID")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  <div className="flex flex-row items-center text-sm text-[#192048]" data-testid="tagsFilterIssues">
                    <div style={{ width: "100%" }} data-testid="tagsTextFilterIssues">
                      <Text>{t("Tags")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.tagData ?? ""}
                      name="tagData"
                      placeholder={t("Tags")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  <div
                    className="flex flex-row items-center text-sm text-[#192048]"
                    data-testid="assignedToFilterIssues"
                  >
                    <div style={{ width: "100%" }} data-testid="assignedToTextFilterIssues">
                      <Text>{t("Assigned To")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.assignedToUserName ?? ""}
                      name="assignedToUserName"
                      placeholder={t("Assigned To")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  {/* <div className={css.filterParentInnerColelem}>
                  <div style={{ width: '100%' }}>{t("Type")}</div>
                  <input className="e-input" name="type" placeholder={t("Type")} onChange={handleSetFilterConditions} />
                </div> */}
                </div>
              </div>
              <div className="flex gap-5" data-testid="btnFilterIssues">
                <ButtonComponent
                  buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                  onClick={() => {
                    onInputChangePred();
                  }}
                >
                  <Text>{t("Apply")}</Text>
                </ButtonComponent>
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BORDER_BUTTON}
                  onClick={() => {
                    setShowFilterIssue(!showFilterIssue);
                  }}
                >
                  <Text>{t("Cancel")}</Text>
                </ButtonComponent>
                <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={resetFilterHandler}>
                  <Text>{t("Reset")}</Text>
                </ButtonComponent>
              </div>
            </div>
          )}
        </div>
      )}

      <div
        id="scroableIssues"
        className="h-auto w-full flex-1 flex-grow overflow-y-auto bg-white"
        style={{
          marginTop: HoopsModalStatus.Issues?.currentStage === "issues" ? "0.25rem" : "0",
        }}
      >
        {["issues"].includes(HoopsModalStatus.Issues?.currentStage ?? "") && (
          <Suspense fallback={<></>}>
            <IssuesList
              onViewIssueClick={onViewIssueClick}
              handleSelectedIssues={handleSelectedIssues}
              fileVersionId={fileVersionId}
              transmittalVersionId={transmittalVersionId}
              selectedIssues={selectedIssues}
              searchString={searchString}
              filterPredicates={filterPredicates}
              sortPredicates={sortPredicates}
              engineType={engineType}
            />
          </Suspense>
        )}
        {["view_issue"].includes(HoopsModalStatus.Issues?.currentStage ?? "") && currentViewIssueId && (
          <Suspense fallback={<></>}>
            <ViewIssue
              currentViewIssueId={currentViewIssueId}
              selectedIssues={selectedIssues}
              fileVersionId={fileVersionId}
              handleSelectedIssues={handleSelectedIssues}
              fileType={fileType}
              engineType={engineType}
              spaceId={urlSearchParams.get("spaceId") || fileVersionData?.spaceId}
            />
          </Suspense>
        )}
        {["create_issue", "edit_issue"].includes(HoopsModalStatus.Issues?.currentStage ?? "") && (
          <Suspense fallback={<></>}>
            <CreateIssue
              fileVersionId={fileVersionId}
              transmittalVersionId={transmittalVersionId}
              fileType={fileType}
              engineType={engineType}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}

export default IssuesPallet;
